import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  FolderOpen,
  Package,
  Plus,
  Trash2,
  Copy,
  Check,
  ArrowLeft,
  Search,
  X,
  Code,
  Lock,
  Globe,
  Users,
} from 'lucide-react'
import { api, Collection, PackageSummary } from '../utils/api'

export default function CollectionDetailPage() {
  const { collectionId } = useParams<{ collectionId: string }>()
  const navigate = useNavigate()
  const [collection, setCollection] = useState<Collection | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<PackageSummary[]>([])
  const [searching, setSearching] = useState(false)
  const [copied, setCopied] = useState(false)
  const [exportConfig, setExportConfig] = useState<string>('')
  const [isOwner, setIsOwner] = useState(false)
  const [updatingVisibility, setUpdatingVisibility] = useState(false)

  const token = localStorage.getItem('auth_token') || ''

  useEffect(() => {
    if (collectionId) {
      loadCollection()
    }
  }, [collectionId])

  const loadCollection = async () => {
    if (!collectionId) return
    setLoading(true)
    setError(null)
    try {
      // Try to load as authenticated user first, then fall back to public
      let data: Collection
      try {
        if (token) {
          data = await api.getCollection(collectionId, token)
          setIsOwner(true) // If we can access via authenticated endpoint, assume ownership
        } else {
          data = await api.getPublicCollection(collectionId)
          setIsOwner(false)
        }
      } catch {
        // Fall back to public endpoint
        data = await api.getPublicCollection(collectionId)
        setIsOwner(false)
      }
      setCollection(data)

      // Load export config
      try {
        const exportData = await api.exportCollection(collectionId, 'claude-desktop', token || 'public')
        setExportConfig(JSON.stringify(exportData.config, null, 2))
      } catch {
        setExportConfig('{}')
      }
    } catch (err) {
      console.error('Failed to load collection:', err)
      setError('Collection not found or is private')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = async () => {
    if (!searchQuery.trim()) return
    setSearching(true)
    try {
      const response = await api.searchPackages({ q: searchQuery, limit: 10 })
      // Filter out packages already in collection
      const existingIds = new Set(collection?.items.map((i) => i.package_id) || [])
      setSearchResults(response.items.filter((p) => !existingIds.has(p.package_id)))
    } catch (err) {
      console.error('Search failed:', err)
    } finally {
      setSearching(false)
    }
  }

  const handleAddPackage = async (pkg: PackageSummary) => {
    if (!collectionId || !token) return
    try {
      const updated = await api.addCollectionItem(token, collectionId, {
        package_id: pkg.package_id,
        version_constraint: '^1.0.0',
      })
      setCollection(updated)
      setSearchResults(searchResults.filter((p) => p.package_id !== pkg.package_id))
      // Refresh export config
      const exportData = await api.exportCollection(collectionId, 'claude-desktop', token)
      setExportConfig(JSON.stringify(exportData.config, null, 2))
    } catch (err) {
      console.error('Failed to add package:', err)
    }
  }

  const handleRemovePackage = async (packageId: string) => {
    if (!collectionId || !token) return
    try {
      await fetch(`/v1/collections/${collectionId}/items/${packageId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      setCollection({
        ...collection!,
        items: collection!.items.filter((i) => i.package_id !== packageId),
      })
      // Refresh export config
      const exportData = await api.exportCollection(collectionId, 'claude-desktop', token)
      setExportConfig(JSON.stringify(exportData.config, null, 2))
    } catch (err) {
      console.error('Failed to remove package:', err)
    }
  }

  const toggleVisibility = async () => {
    if (!collectionId || !token || !collection) return
    setUpdatingVisibility(true)
    try {
      const updated = await api.updateCollection(token, collectionId, {
        is_public: !collection.is_public,
      })
      setCollection(updated)
    } catch (err) {
      console.error('Failed to update visibility:', err)
    } finally {
      setUpdatingVisibility(false)
    }
  }

  const copyConfig = async () => {
    await navigator.clipboard.writeText(exportConfig)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleDelete = async () => {
    if (!collectionId || !token) return
    if (!confirm('Are you sure you want to delete this collection?')) return

    try {
      await fetch(`/v1/collections/${collectionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      navigate('/collections')
    } catch (err) {
      console.error('Failed to delete:', err)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-8" />
            <div className="h-48 bg-gray-200 rounded-lg mb-6" />
            <div className="h-64 bg-gray-200 rounded-lg" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !collection) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Collection not found
          </h2>
          <p className="text-gray-500 mb-6">
            {error || "The collection you're looking for doesn't exist or is private."}
          </p>
          <Link to="/collections" className="btn btn-primary">
            Back to Collections
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <Link
            to="/collections"
            className="flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Collections
          </Link>
        </nav>

        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div className="flex items-center">
            <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center mr-4">
              <FolderOpen className="w-8 h-8 text-primary-600" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-gray-900">{collection.name}</h1>
                {collection.is_public ? (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                    <Globe className="w-3 h-3 mr-1" />
                    Public
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    <Lock className="w-3 h-3 mr-1" />
                    Private
                  </span>
                )}
              </div>
              {collection.description && (
                <p className="text-gray-600 mt-1">{collection.description}</p>
              )}
              {collection.owner_name && (
                <p className="text-sm text-gray-400 mt-1 flex items-center">
                  <Users className="w-4 h-4 mr-1" />
                  by {collection.owner_name}
                </p>
              )}
            </div>
          </div>
          {isOwner && (
            <div className="flex items-center gap-2">
              <button
                onClick={toggleVisibility}
                disabled={updatingVisibility}
                className="btn btn-secondary"
              >
                {collection.is_public ? (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Make Private
                  </>
                ) : (
                  <>
                    <Globe className="w-4 h-4 mr-2" />
                    Make Public
                  </>
                )}
              </button>
              <button
                onClick={handleDelete}
                className="btn btn-secondary text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </button>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Tools list */}
          <div className="lg:col-span-2">
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Skills ({collection.items.length})
                </h2>
                {isOwner && (
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="btn btn-primary btn-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Skill
                  </button>
                )}
              </div>

              {collection.items.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 mb-4">No skills in this collection</p>
                  {isOwner && (
                    <button
                      onClick={() => setShowAddModal(true)}
                      className="btn btn-secondary"
                    >
                      Add your first skill
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {collection.items.map((item) => (
                    <div
                      key={item.package_id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <Link
                        to={`/skills/${item.package_slug || item.package_id}`}
                        className="flex items-center flex-1 min-w-0"
                      >
                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                          <Package className="w-5 h-5 text-primary-600" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900 hover:text-primary-600 truncate">
                              {item.package_name || item.package_id}
                            </p>
                            {item.open_source && (
                              <Code className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                            )}
                          </div>
                          {item.package_summary && (
                            <p className="text-sm text-gray-500 truncate">
                              {item.package_summary}
                            </p>
                          )}
                          <p className="text-xs text-gray-400">
                            {item.version_constraint || 'latest'}
                          </p>
                        </div>
                      </Link>
                      {isOwner && (
                        <button
                          onClick={() => handleRemovePackage(item.package_id)}
                          className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-white ml-2"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Export config */}
          <div>
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Export</h2>
                <button
                  onClick={copyConfig}
                  className="btn btn-secondary btn-sm"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-1 text-green-500" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Add this to your Claude Desktop config:
              </p>
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm text-gray-300 font-mono whitespace-pre-wrap">
                  {exportConfig || '{}'}
                </pre>
              </div>
            </div>
          </div>
        </div>

        {/* Add skill modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Add Skill</h2>
                <button
                  onClick={() => {
                    setShowAddModal(false)
                    setSearchQuery('')
                    setSearchResults([])
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex gap-2 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Search for skills..."
                    className="input pl-10 w-full"
                  />
                </div>
                <button
                  onClick={handleSearch}
                  disabled={searching}
                  className="btn btn-primary"
                >
                  {searching ? 'Searching...' : 'Search'}
                </button>
              </div>

              <div className="max-h-80 overflow-y-auto">
                {searchResults.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">
                    {searchQuery ? 'No skills found' : 'Search for skills to add'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {searchResults.map((pkg) => (
                      <div
                        key={pkg.package_id}
                        className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-primary-300"
                      >
                        <div className="flex items-center flex-1 min-w-0">
                          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                            <Package className="w-5 h-5 text-primary-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                              {pkg.name}
                            </p>
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-gray-500 truncate">{pkg.slug}</span>
                              {pkg.open_source ? (
                                <span className="flex items-center text-emerald-600">
                                  <Code className="w-3 h-3 mr-0.5" />
                                  OSS
                                </span>
                              ) : (
                                <span className="flex items-center text-gray-400">
                                  <Lock className="w-3 h-3 mr-0.5" />
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddPackage(pkg)}
                          className="btn btn-secondary btn-sm ml-2"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
